import React, { useRef, useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import FullScreenVideoRecorder from '../../../../../apps/pro/views/Upload/VideoRecorder/components/FullScreenVideoRecorder';
import useVideoRecording from '../../../../../hooks/useVideoRecording';
import { getErrorString } from '../../../../../util/normalize-errors';
import Button from '../../../../../components/global/Button';
import Angle from '../../../../../components/global/Angle';
import { getCelebratedPersonName } from '../../../../../apps/pro/views/AlbumFolder/FreemiumEvents/SpecialtyAlbumGreetingModal/helpers';
import videoExtensions from '../../../../../apps/pro/views/Upload/VideoUpload/helpers';
import { isSpecialtyAlbum } from '../../../../../util/album';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  authenticated: PropTypes.bool,
  onUpload: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  backButtonLabel: PropTypes.string,
  uploadOnly: PropTypes.bool,
};

const videoExtensionsType = videoExtensions
  .map(extension => `.${extension}`)
  .join(',');

const VideoUploadStep = ({
  album,
  albumFolder,
  authenticated,
  onUpload,
  onBack,
  backButtonLabel = 'Back to Album Selection',
  uploadOnly = false,
}) => {
  const ref = useRef();
  const history = useHistory();
  const [error, setError] = useState(null);
  const [recordingComplete, setRecordingComplete] = useState(false);
  const {
    getCameraPermission,
    loadingDevices,
    cameraStream,
    onRecordingStart,
    onRecordingStop,
    recording,
    cameraPermission,
    remainingTime,
    recordedVideoData,
    onDeviceChange,
    onRecordingReset,
    devices,
  } = useVideoRecording({
    onError: err => {
      setError(getErrorString(err));
    },
    storeKey: 'VideoRecording',
  });

  useEffect(() => {
    if (!uploadOnly) {
      getCameraPermission();
    }
  }, [uploadOnly]);

  const recordedVideoUrl = recordedVideoData?.current?.url;

  const possessiveCelebratedName = getCelebratedPersonName(
    albumFolder?.album_subtype,
    albumFolder?.event_settings,
    true,
    albumFolder?.account?.organization_name ?? albumFolder?.account?.name,
  );
  const photosAlbums = (albumFolder?.children?.albums?.records ?? []).filter(
    child => !isSpecialtyAlbum(child),
  );

  return (
    <Fragment>
      <div className="d-flex flex-column align-items-center justify-content-center">
        <h2 className="text-center mb-4">
          Add your videos to <br />
          {possessiveCelebratedName} <br className="d-block d-md-none" />
          <span className="text-primary">{album.name}</span>
        </h2>
      </div>
      {!error && cameraPermission && !uploadOnly && !recordingComplete && (
        <FullScreenVideoRecorder
          loadingDevices={loadingDevices}
          cameraStream={cameraStream}
          onRecordingStart={onRecordingStart}
          onRecordingStop={onRecordingStop}
          recording={recording}
          recordedVideoUrl={recordedVideoUrl}
          maxRecordingTime={30000}
          remainingTime={remainingTime}
          onDeviceChange={devices.length > 1 ? onDeviceChange : undefined}
          onRecordingReset={onRecordingReset}
          onContinue={() => {
            setRecordingComplete(true);
          }}
          onBack={onBack}
        />
      )}
      {recordedVideoUrl ? (
        <div className="video-recording-playback">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video playsInline src={recordedVideoUrl} controls />
          <div className="d-flex flex-column flex-md-row mt-2">
            <Button
              btnOutline
              btnBlock
              onClick={() => {
                onRecordingReset();
                setRecordingComplete(false);
              }}
              className="text-extra-bold mr-md-1"
            >
              Retake
            </Button>
            <Button
              btnBlock
              onClick={() => {
                onUpload([recordedVideoData.current.file], album);
              }}
              className="text-extra-bold ml-md-1 mt-md-0"
            >
              Upload
            </Button>
          </div>
        </div>
      ) : (
        <Fragment>
          <Button
            btnOutline
            className="text-white-space-normal"
            onClick={() => ref?.current?.click?.()}
          >
            Select a video
          </Button>
          {photosAlbums.length > 1 && (
            <Button
              btnBlock
              btnStyle="link"
              className="mt-2 text-center"
              onClick={() => {
                if (authenticated) {
                  history.push(`/app/shutterbug/folders/${albumFolder?.id}`);
                } else {
                  history.push(`/galleries/view/folder/${albumFolder?.id}`);
                }
              }}
            >
              Want to add a video to another album?
            </Button>
          )}
          <input
            id="uploader"
            type="file"
            className="d-none"
            accept={videoExtensionsType}
            onChange={event => {
              onUpload([...event.target.files], album, true);
            }}
            ref={ref}
          />
        </Fragment>
      )}
      {error && <p className="text-danger text-center">{error}</p>}
      {onBack && (
        <div className="text-center mt-auto">
          <Button btnStyle="link" onClick={onBack}>
            <Angle direction="left" className="mr-1" /> {backButtonLabel}
          </Button>
        </div>
      )}
    </Fragment>
  );
};

VideoUploadStep.propTypes = propTypes;

export default VideoUploadStep;
